import type {
  DashboardFilterPanel,
  DashboardSlicer,
  SemanticFilter,
  AnalyticsBootstrap,
} from '@lognai/plugin-analytics';

export const emptyFilterPanel = (): DashboardFilterPanel => ({
  enabled: false,
  position: 'left',
  width: 260,
  startCollapsed: false,
  showCounts: false,
  contextualValues: false,
  showDatePreset: true,
  slicers: [],
});
export const defaultSlicerFilters = (slicer: DashboardSlicer): SemanticFilter[] =>
  slicer.control === 'date_range'
    ? dateSlicerFilters(slicer.member, slicer.defaultStart ?? '', slicer.defaultEnd ?? '')
    : slicer.defaultValues.length
      ? [{ member: slicer.member, operator: 'equals', values: slicer.defaultValues }]
      : [];
export const dateSlicerFilters = (member: string, start: string, end: string): SemanticFilter[] => {
  if (start && end) return [{ member, operator: 'inDateRange', values: [start, end] }];
  if (start) return [{ member, operator: 'gte', values: [`${start}T00:00:00.000Z`] }];
  if (end) return [{ member, operator: 'lte', values: [`${end}T23:59:59.999Z`] }];
  return [];
};
export const slicerDateBounds = (filters: SemanticFilter[], member: string): [string, string] => {
  const range = filters.find((f) => f.member === member && f.operator === 'inDateRange');
  return range
    ? [range.values?.[0]?.slice(0, 10) ?? '', range.values?.[1]?.slice(0, 10) ?? '']
    : [
        filters
          .find((f) => f.member === member && f.operator === 'gte')
          ?.values?.[0]?.slice(0, 10) ?? '',
        filters
          .find((f) => f.member === member && f.operator === 'lte')
          ?.values?.[0]?.slice(0, 10) ?? '',
      ];
};
export const initialDashboardFilters = (
  filters: SemanticFilter[],
  panel?: DashboardFilterPanel
): SemanticFilter[] => {
  if (!panel?.enabled) return [...filters];
  const members = new Set(filters.map((filter) => filter.member));
  return [
    ...filters,
    ...panel.slicers.filter((s) => !members.has(s.member)).flatMap(defaultSlicerFilters),
  ];
};
/** One equals filter per field gives OR within selections and AND between fields. */
export const toggleDashboardFilter = (
  filters: SemanticFilter[],
  selection: SemanticFilter
): SemanticFilter[] => {
  if (selection.operator !== 'equals')
    return [...filters.filter((f) => f.member !== selection.member), selection];
  const existing =
    filters.find((f) => f.member === selection.member && f.operator === 'equals')?.values ?? [];
  const values = new Set(existing);
  for (const value of selection.values ?? [])
    values.has(value) ? values.delete(value) : values.add(value);
  return [
    ...filters.filter((f) => f.member !== selection.member),
    ...(values.size
      ? [{ member: selection.member, operator: 'equals' as const, values: [...values] }]
      : []),
  ];
};
export const filterPanelErrors = (
  panel: DashboardFilterPanel | undefined,
  catalog: AnalyticsBootstrap['semanticCatalog'],
  targets?: string[]
): string[] => {
  if (!panel) return [];
  const errors: string[] = [];
  const seen = new Set<string>();
  for (const slicer of panel.slicers) {
    const source = catalog[slicer.member.split('.')[0]];
    if (targets && slicer.targets?.some((id) => !targets.includes(id)))
      errors.push(`Remove unavailable filter targets for ${slicer.label}.`);
    if (slicer.targets && !slicer.targets.length)
      errors.push(`Choose at least one target for ${slicer.label}.`);
    if (!slicer.label.trim()) errors.push('Every filter needs a label.');
    if (
      !source ||
      (slicer.control === 'date_range'
        ? !source.timeDimensions.includes(slicer.member)
        : !source.dimensions.includes(slicer.member) ||
          source.timeDimensions.includes(slicer.member))
    )
      errors.push(
        `Choose an available ${slicer.control === 'date_range' ? 'date' : 'category'} field for ${slicer.label || 'this filter'}.`
      );
    if (seen.has(slicer.member)) errors.push('Each field can have only one filter control.');
    seen.add(slicer.member);
    if (slicer.defaultStart && slicer.defaultEnd && slicer.defaultStart > slicer.defaultEnd)
      errors.push(`Check the default date range for ${slicer.label}.`);
    if (slicer.defaultValues.length > 50)
      errors.push(`Choose at most 50 defaults for ${slicer.label}.`);
  }
  return errors;
};

export const filtersForTarget = (
  filters: SemanticFilter[],
  panel: DashboardFilterPanel | undefined,
  target: string
): SemanticFilter[] =>
  filters.filter((filter) => {
    const slicer = panel?.slicers.find((s) => s.member === filter.member);
    return !slicer?.targets || slicer.targets.includes(target);
  });

export const savedDashboardFilters = (
  filters: SemanticFilter[],
  previous: DashboardFilterPanel | undefined,
  next: DashboardFilterPanel | undefined
): SemanticFilter[] => {
  let saved = [...filters];
  for (const slicer of previous?.slicers ?? []) {
    const replacement = next?.slicers.find((s) => s.member === slicer.member);
    if (
      !replacement ||
      JSON.stringify(defaultSlicerFilters(slicer)) !==
        JSON.stringify(defaultSlicerFilters(replacement))
    )
      saved = saved.filter((f) => f.member !== slicer.member);
  }
  return initialDashboardFilters(saved, next);
};
export const filterDisplayValue = (filter: SemanticFilter) => {
  if (filter.operator === 'inDateRange')
    return `${filter.values?.[0]?.slice(0, 10)} – ${filter.values?.[1]?.slice(0, 10)}`;
  if (filter.operator === 'gte' && filter.values?.[0]?.includes('T'))
    return `From ${filter.values[0].slice(0, 10)}`;
  if (filter.operator === 'lte' && filter.values?.[0]?.includes('T'))
    return `To ${filter.values[0].slice(0, 10)}`;
  return filter.values?.join(', ') ?? filter.operator;
};
